import { renderPcard, renderPcardSkeleton } from '@/components/pcard';
import { icons } from '@/components/icons';
import {
  renderFilterChips,
  renderFilterPopover,
  renderFilterSheet,
  type FilterOption,
  type FilterSection,
} from '@/components/bottom-sheet';
import { listCategories } from '@/api/categories';
import { listDestinations, buildDestinationMap } from '@/api/destinations';
import { listProducts } from '@/api/products';
import { getVenueMap } from '@/api/venues';
import { HojApiError } from '@/api/client';
import { defaultFilters, filtersStore, selectionStore } from '@/lib/store';
import type { Category, Destination, Product, Venue } from '@/api/types';
import type { Screen } from '@/router/router';

interface ListingState {
  products: Product[];
  filtered: Product[];
  categories: Category[];
  destinations: Destination[];
  destinationMap: Map<string, Destination>;
  venues: Map<string, Venue>;
  loading: boolean;
  enriching: boolean;
  error?: string;
}

function priceBucketMatches(p: Product, bucket: string): boolean {
  if (bucket === 'all') return true;
  if (p.price == null) return false;
  const v = p.price;
  switch (bucket) {
    case 'u500':
      return v < 500;
    case '500-900':
      return v >= 500 && v < 900;
    case '900-1300':
      return v >= 900 && v < 1300;
    case 'o1300':
      return v >= 1300;
    default:
      return true;
  }
}

function durationBucketMatches(p: Product, bucket: string): boolean {
  if (bucket === 'all') return true;
  const days = p.defaultDurationInDays ?? 0;
  switch (bucket) {
    case 'weekend':
      return days >= 1 && days <= 2;
    case 'short':
      return days >= 3 && days <= 4;
    case 'week':
      return days >= 5 && days <= 7;
    case 'long':
      return days >= 8;
    default:
      return true;
  }
}

const SEASON_MONTHS: Record<string, number[]> = {
  spring: [2, 3, 4],
  summer: [5, 6, 7],
  autumn: [8, 9, 10],
  winter: [11, 0, 1],
};

function seasonMatches(p: Product, bucket: string): boolean {
  if (bucket === 'all') return true;
  const months = SEASON_MONTHS[bucket];
  if (!months) return true;
  const dates: string[] = [];
  if (p.minDate) dates.push(p.minDate);
  if (p.maxDate) dates.push(p.maxDate);
  for (const a of p.availabilities ?? []) {
    if (a.startDate) dates.push(a.startDate);
  }
  if (dates.length === 0) return true;
  return dates.some((iso) => {
    const m = new Date(iso).getMonth();
    return Number.isFinite(m) && months.includes(m);
  });
}

/** The category filter sends the Category.id; production data has been seen to
 * carry either the id OR the slug on the product, so we match both to be safe.
 */
function categoryMatches(p: Product, bucket: string, categories: Category[]): boolean {
  if (bucket === 'all') return true;
  if (p.categoryId === bucket) return true;
  const cat = categories.find((c) => c.id === bucket);
  if (cat && p.categoryId && cat.slug === p.categoryId) return true;
  return false;
}

/** Where bucket can be:
 *  - 'all'                → no restriction
 *  - 'c:<countryCode>'    → all products whose destination belongs to that country
 *  - '<destinationId>'    → exact destination match
 */
function whereMatches(
  p: Product,
  bucket: string,
  destinationMap: Map<string, Destination>,
): boolean {
  if (bucket === 'all') return true;
  if (bucket.startsWith('c:')) {
    if (!p.destinationId) return false;
    const dest = destinationMap.get(p.destinationId);
    return (dest?.country ?? '').trim() === bucket.slice(2);
  }
  return p.destinationId === bucket;
}

function queryMatches(
  p: Product,
  q: string,
  destinationMap: Map<string, Destination>,
  venueMap: Map<string, Venue>,
): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const dest = p.destinationId ? destinationMap.get(p.destinationId) : undefined;
  const venue = p.venueId ? venueMap.get(p.venueId) : undefined;
  const haystack = [
    p.title,
    p.shortDescription ?? '',
    p.description ?? '',
    p.tripCode ?? '',
    dest?.title ?? '',
    dest?.country ?? '',
    venue?.title ?? '',
  ]
    .join(' ')
    .toLowerCase();
  return needle.split(/\s+/).every((token) => haystack.includes(token));
}

function applyFilters(
  products: Product[],
  f: typeof defaultFilters,
  categories: Category[],
  destinationMap: Map<string, Destination>,
  venueMap: Map<string, Venue>,
): Product[] {
  return products.filter(
    (p) =>
      categoryMatches(p, f.country, categories) &&
      whereMatches(p, f.where, destinationMap) &&
      seasonMatches(p, f.date) &&
      priceBucketMatches(p, f.price) &&
      durationBucketMatches(p, f.duration) &&
      queryMatches(p, f.q, destinationMap, venueMap),
  );
}

function buildFilterSections(
  categories: Category[],
  destinations: Destination[],
  products: Product[],
): FilterSection[] {
  // Restrict options to values actually referenced by at least one product.
  const usedDestinationIds = new Set<string>();
  const usedCategoryIds = new Set<string>();
  for (const p of products) {
    if (p.destinationId) usedDestinationIds.add(p.destinationId);
    if (p.categoryId) usedCategoryIds.add(p.categoryId);
  }

  // Dedup + restrict destinations.
  const dedupDest = new Map<string, Destination>();
  for (const d of destinations) {
    if (!d.id) continue;
    if (!usedDestinationIds.has(d.id)) continue;
    if (!dedupDest.has(d.id)) dedupDest.set(d.id, d);
  }
  const usedDests = [...dedupDest.values()];

  // Group destinations by country.
  const byCountry = new Map<string, Destination[]>();
  const noCountry: Destination[] = [];
  for (const d of usedDests) {
    const c = (d.country ?? '').trim();
    if (!c) {
      noCountry.push(d);
      continue;
    }
    const arr = byCountry.get(c) ?? [];
    arr.push(d);
    byCountry.set(c, arr);
  }
  // Sort countries and destinations alphabetically.
  const sortedCountries = [...byCountry.keys()].sort((a, b) =>
    a.localeCompare(b, 'it'),
  );
  for (const c of sortedCountries) {
    byCountry.get(c)?.sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '', 'it'));
  }
  noCountry.sort((a, b) => (a.title ?? '').localeCompare(b.title ?? '', 'it'));

  // Restrict categories the same way (also accept slug-based categoryId).
  const usableCategories = categories.filter(
    (c) => usedCategoryIds.has(c.id) || usedCategoryIds.has(c.slug),
  );

  const sections: FilterSection[] = [];
  if (usedDests.length > 0) {
    const whereOptions: FilterOption[] = [{ value: 'all', label: 'Ovunque' }];
    for (const country of sortedCountries) {
      const dests = byCountry.get(country) ?? [];
      // Country roll-up option only when the country has 2+ destinations
      // (otherwise the per-destination row already filters everything).
      if (dests.length > 1) {
        whereOptions.push({
          value: `c:${country}`,
          label: `Tutto ${country}`,
          group: country,
          emphasized: true,
        });
      }
      for (const d of dests) {
        whereOptions.push({
          value: d.id,
          label: d.title,
          group: country,
          nested: dests.length > 1,
        });
      }
    }
    if (noCountry.length > 0) {
      for (const d of noCountry) {
        whereOptions.push({
          value: d.id,
          label: d.title,
          group: 'Altre destinazioni',
        });
      }
    }
    sections.push({
      key: 'where',
      title: 'Dove',
      options: whereOptions,
    });
  }
  if (usableCategories.length > 0) {
    sections.push({
      key: 'country',
      title: 'Tipo',
      options: [
        { value: 'all', label: 'Tutti i tipi' },
        ...usableCategories.map((c) => ({ value: c.id, label: c.name })),
      ],
    });
  }
  sections.push(
    {
      key: 'date',
      title: 'Periodo',
      options: [
        { value: 'all', label: 'Sempre' },
        { value: 'spring', label: 'Primavera (Mar-Mag)' },
        { value: 'summer', label: 'Estate (Giu-Ago)' },
        { value: 'autumn', label: 'Autunno (Set-Nov)' },
        { value: 'winter', label: 'Inverno (Dic-Feb)' },
      ],
    },
    {
      key: 'price',
      title: 'Prezzo',
      options: [
        { value: 'all', label: 'Tutti i prezzi' },
        { value: 'u500', label: 'Sotto €500' },
        { value: '500-900', label: '€500 – €900' },
        { value: '900-1300', label: '€900 – €1.300' },
        { value: 'o1300', label: '€1.300+' },
      ],
    },
    {
      key: 'duration',
      title: 'Durata',
      options: [
        { value: 'all', label: 'Qualsiasi' },
        { value: 'weekend', label: 'Weekend (1-2g)' },
        { value: 'short', label: 'Corto (3-4g)' },
        { value: 'week', label: 'Settimana (5-7g)' },
        { value: 'long', label: 'Lungo (8+g)' },
      ],
    },
  );
  return sections;
}

function enrichWithVenues(products: Product[], venues: Map<string, Venue>): Product[] {
  if (venues.size === 0) return products;
  return products.map((p) => {
    if (p.coverUrl || !p.venueId) return p;
    const venue = venues.get(p.venueId);
    return venue?.coverUrl ? { ...p, coverUrl: venue.coverUrl } : p;
  });
}

export function createListingScreen(): Screen {
  let rootEl!: HTMLElement;
  let chipsEl!: HTMLElement;
  let listEl!: HTMLElement;
  let resultEl!: HTMLElement;
  let resetEl!: HTMLElement;
  let sheetHostEl!: HTMLElement;
  let popoverHostEl!: HTMLElement;
  let searchBarEl!: HTMLElement;
  let searchInputEl!: HTMLInputElement;
  let searchOpen = false;
  let searchDebounceId: number | undefined;
  let goToProduct!: (id: string) => void;
  let sections: FilterSection[] = buildFilterSections([], [], []);

  const state: ListingState = {
    products: [],
    filtered: [],
    categories: [],
    destinations: [],
    destinationMap: new Map(),
    venues: new Map(),
    loading: true,
    enriching: false,
  };

  function refilter(): void {
    state.filtered = applyFilters(
      state.products,
      filtersStore.get(),
      state.categories,
      state.destinationMap,
      state.venues,
    );
  }

  function renderResults(): void {
    if (state.loading) {
      listEl.innerHTML = renderPcardSkeleton(6);
      resultEl.textContent = 'Caricamento…';
      return;
    }
    if (state.error) {
      listEl.innerHTML = `
        <div class="state-msg">
          <h3>Impossibile caricare i viaggi</h3>
          <p>${state.error}</p>
          <button class="btn btn-navy" data-action="retry" type="button">Riprova</button>
        </div>`;
      resultEl.textContent = '';
      wireCards();
      return;
    }
    if (state.filtered.length === 0) {
      listEl.innerHTML = `
        <div class="state-msg">
          <h3>Nessun viaggio con questi filtri</h3>
          <p>Modifica o azzera i filtri per vedere tutti i viaggi disponibili.</p>
          <button class="btn btn-navy" data-action="reset-filters" type="button">Reset filtri</button>
        </div>`;
      resultEl.textContent = '0 viaggi';
      wireCards();
      return;
    }
    listEl.innerHTML = state.filtered.map(renderPcard).join('');
    resultEl.textContent = `${state.filtered.length} ${state.filtered.length === 1 ? 'viaggio trovato' : 'viaggi trovati'}`;
    wireCards();
  }

  /** Update only the <img> covers (used when venue map arrives in background). */
  function patchCoversInPlace(): void {
    listEl.querySelectorAll<HTMLElement>('.pcard').forEach((card) => {
      const id = card.dataset.id;
      const p = state.products.find((x) => x.id === id);
      if (!p?.coverUrl) return;
      const heroEl = card.querySelector<HTMLElement>('.pcard-hero');
      if (!heroEl) return;
      let img = heroEl.querySelector<HTMLImageElement>('.pcard-hero-img');
      if (!img) {
        img = document.createElement('img');
        img.className = 'pcard-hero-img';
        img.loading = 'lazy';
        img.referrerPolicy = 'no-referrer';
        img.alt = '';
        img.onerror = () => {
          img?.remove();
        };
        heroEl.appendChild(img);
      }
      if (img.src !== p.coverUrl) {
        img.src = p.coverUrl;
      }
    });
  }

  function wireCards(): void {
    listEl.querySelectorAll<HTMLImageElement>('.pcard-hero-img').forEach((img) => {
      img.addEventListener('error', () => {
        img.remove();
      });
    });
    listEl.querySelectorAll<HTMLButtonElement>('.pcard').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.id;
        if (!id) return;
        const product = state.products.find((p) => p.id === id);
        if (!product) return;
        selectionStore.set({ product });
        goToProduct(id);
      });
    });
    listEl.querySelectorAll<HTMLButtonElement>('[data-action="retry"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        void load();
      });
    });
    listEl.querySelectorAll<HTMLButtonElement>('[data-action="reset-filters"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        filtersStore.replace({ ...defaultFilters });
        refilter();
        renderResults();
        refreshFilterUi();
      });
    });
  }

  function refreshFilterUi(): void {
    chipsEl.innerHTML = renderFilterChips(sections, filtersStore.get());
    wireChips();
    const hasActive = Object.entries(filtersStore.get()).some(
      ([k, v]) => k in defaultFilters && v !== defaultFilters[k],
    );
    resetEl.classList.toggle('show', hasActive);
  }

  function wireChips(): void {
    chipsEl.querySelectorAll<HTMLButtonElement>('.fchip[data-key]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const key = btn.dataset.key;
        if (!key) return;
        openPopover(key, btn);
      });
    });
    chipsEl.querySelectorAll<HTMLButtonElement>('.fchip-clear').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const key = btn.dataset.key;
        if (!key) return;
        filtersStore.set({ [key]: 'all' } as Record<string, string>);
        refilter();
        renderResults();
        refreshFilterUi();
      });
    });
    chipsEl.querySelector<HTMLButtonElement>('[data-action="open-all-filters"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      openSheet();
    });
    chipsEl.querySelector<HTMLButtonElement>('[data-action="open-search"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleSearch(true);
    });
    chipsEl.querySelector<HTMLButtonElement>('[data-action="clear-search"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      setQuery('');
    });
  }

  function setQuery(q: string): void {
    filtersStore.set({ q });
    refilter();
    renderResults();
    refreshFilterUi();
  }

  function toggleSearch(open: boolean): void {
    searchOpen = open;
    searchBarEl.classList.toggle('open', open);
    if (open) {
      searchInputEl.value = filtersStore.get().q ?? '';
      requestAnimationFrame(() => searchInputEl.focus());
    } else {
      searchInputEl.blur();
    }
  }

  function closePopover(): void {
    popoverHostEl.innerHTML = '';
    document.removeEventListener('click', onDocClick, true);
  }

  function onDocClick(e: Event): void {
    const target = e.target as Element | null;
    if (target && target.closest('.fpop')) return;
    closePopover();
  }

  function openPopover(key: string, anchor: HTMLElement): void {
    const section = sections.find((s) => s.key === key);
    if (!section) return;
    const currentVal = filtersStore.get()[key] ?? 'all';
    const rect = anchor.getBoundingClientRect();
    const hostRect = rootEl.getBoundingClientRect();
    const top = rect.bottom - hostRect.top + 6;
    const left = Math.max(12, Math.min(rect.left - hostRect.left, hostRect.width - 240));
    popoverHostEl.innerHTML = renderFilterPopover({
      section,
      selected: currentVal,
      top,
      left,
    });

    const pop = popoverHostEl.querySelector<HTMLElement>('.fpop');
    if (!pop) return;
    requestAnimationFrame(() => pop.classList.add('open'));

    pop.querySelectorAll<HTMLButtonElement>('.fpop-opt').forEach((opt) => {
      opt.addEventListener('click', () => {
        const val = opt.dataset.val;
        if (!val) return;
        filtersStore.set({ [key]: val } as Record<string, string>);
        refilter();
        renderResults();
        refreshFilterUi();
        closePopover();
      });
    });
    pop.querySelector<HTMLButtonElement>('[data-action="clear"]')?.addEventListener('click', () => {
      filtersStore.set({ [key]: 'all' } as Record<string, string>);
      refilter();
      renderResults();
      refreshFilterUi();
      closePopover();
    });

    setTimeout(() => document.addEventListener('click', onDocClick, true), 0);
  }

  function openSheet(): void {
    closePopover();
    const working: Record<string, string> = { ...filtersStore.get() };
    let previewCount = state.filtered.length;

    const render = (): void => {
      sheetHostEl.innerHTML = renderFilterSheet({
        sections,
        selected: working,
        applyLabel: `Mostra ${previewCount} ${previewCount === 1 ? 'viaggio' : 'viaggi'}`,
      });
      wire();
    };

    const recalc = (): void => {
      const preview = applyFilters(
        state.products,
        { ...filtersStore.get(), ...working } as typeof defaultFilters,
        state.categories,
        state.destinationMap,
        state.venues,
      );
      previewCount = preview.length;
      const applyBtn = sheetHostEl.querySelector<HTMLButtonElement>('[data-action="apply"]');
      if (applyBtn) {
        applyBtn.textContent = `Mostra ${previewCount} ${previewCount === 1 ? 'viaggio' : 'viaggi'}`;
      }
    };

    const wire = (): void => {
      const overlay = sheetHostEl.querySelector<HTMLElement>('[data-role="overlay"]');
      if (!overlay) return;
      requestAnimationFrame(() => overlay.classList.add('open'));

      const close = (): void => {
        overlay.classList.remove('open');
        setTimeout(() => {
          sheetHostEl.innerHTML = '';
        }, 280);
      };

      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) close();
      });
      overlay.querySelector<HTMLButtonElement>('[data-action="close"]')?.addEventListener('click', close);

      overlay.querySelectorAll<HTMLButtonElement>('.fopt').forEach((opt) => {
        opt.addEventListener('click', () => {
          const group = opt.dataset.group;
          const val = opt.dataset.val;
          if (!group || !val) return;
          working[group] = val;
          overlay
            .querySelectorAll<HTMLButtonElement>(`.fopt[data-group="${group}"]`)
            .forEach((sib) => {
              sib.classList.toggle('sel', sib.dataset.val === val);
            });
          recalc();
        });
      });

      overlay.querySelector<HTMLButtonElement>('[data-action="reset"]')?.addEventListener('click', () => {
        for (const k of Object.keys(working)) working[k] = 'all';
        overlay
          .querySelectorAll<HTMLButtonElement>('.fopt')
          .forEach((b) => b.classList.toggle('sel', b.dataset.val === 'all'));
        recalc();
      });

      overlay.querySelector<HTMLButtonElement>('[data-action="apply"]')?.addEventListener('click', () => {
        filtersStore.replace({
          country: working.country ?? 'all',
          group: working.group ?? 'all',
          price: working.price ?? 'all',
          date: working.date ?? 'all',
          duration: working.duration ?? 'all',
          where: working.where ?? 'all',
          q: filtersStore.get().q,
        });
        refilter();
        renderResults();
        refreshFilterUi();
        close();
      });
    };

    render();
  }

  async function load(): Promise<void> {
    state.loading = true;
    state.error = undefined;
    renderResults();

    // Phase A — fire products immediately. Render as soon as we have anything.
    const productsP = listProducts({ limit: 24 });

    // Phase B — categories + destinations + venues in background (don't block initial render).
    const categoriesP = listCategories().catch(() => [] as Category[]);
    const destinationsP = listDestinations().catch(() => [] as Destination[]);
    const venuesP = getVenueMap().catch(() => new Map<string, Venue>());

    try {
      const page = await productsP;
      state.products = page.data.filter((p) => !p.archived);
      refilter();
      state.loading = false;
      state.enriching = true;
      renderResults();
    } catch (err) {
      state.error =
        err instanceof HojApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Errore di rete';
      state.products = [];
      state.filtered = [];
      state.loading = false;
      renderResults();
      return;
    }

    // Phase B resolves — patch covers + filter UI in place.
    void Promise.all([categoriesP, destinationsP, venuesP]).then(([cats, dests, venues]) => {
      state.categories = cats;
      state.destinations = dests;
      state.destinationMap = buildDestinationMap(dests);
      state.venues = venues;
      sections = buildFilterSections(cats, dests, state.products);
      state.products = enrichWithVenues(state.products, venues);
      refilter();
      state.enriching = false;
      patchCoversInPlace();
      renderResults();
      refreshFilterUi();
    });
  }

  return {
    id: 'listing',
    async mount({ el, go }) {
      rootEl = el;
      goToProduct = (id) => {
        void go('product-detail', { params: { productId: id } });
      };

      rootEl.innerHTML = `
        <div class="filter-bar">
          <div class="search-bar" data-role="search-bar">
            <span class="search-bar-icon" aria-hidden="true">${icons.search(16, 'currentColor')}</span>
            <input
              class="search-bar-input"
              data-role="search-input"
              type="search"
              autocomplete="off"
              autocorrect="off"
              spellcheck="false"
              placeholder="Cerca per parola, città, struttura…"
              enterkeyhint="search"
            />
            <button class="search-bar-clear" data-action="search-input-clear" type="button" aria-label="Svuota">×</button>
            <button class="search-bar-close" data-action="search-close" type="button" aria-label="Chiudi ricerca">Annulla</button>
          </div>
          <div class="fchips-row" data-role="chips"></div>
          <div class="filter-meta">
            <span class="filter-result-txt" data-role="result"></span>
            <button class="filter-reset-btn" type="button" data-action="reset-all">Reset</button>
          </div>
        </div>
        <div class="scroll">
          <div class="product-list" data-role="list"></div>
        </div>
        <div data-role="popover-host" class="fpop-host"></div>
        <div data-role="sheet-host"></div>
      `;

      chipsEl = rootEl.querySelector<HTMLElement>('[data-role="chips"]')!;
      listEl = rootEl.querySelector<HTMLElement>('[data-role="list"]')!;
      resultEl = rootEl.querySelector<HTMLElement>('[data-role="result"]')!;
      resetEl = rootEl.querySelector<HTMLElement>('[data-action="reset-all"]')!;
      sheetHostEl = rootEl.querySelector<HTMLElement>('[data-role="sheet-host"]')!;
      popoverHostEl = rootEl.querySelector<HTMLElement>('[data-role="popover-host"]')!;
      searchBarEl = rootEl.querySelector<HTMLElement>('[data-role="search-bar"]')!;
      searchInputEl = rootEl.querySelector<HTMLInputElement>('[data-role="search-input"]')!;

      resetEl.addEventListener('click', () => {
        filtersStore.replace({ ...defaultFilters });
        refilter();
        renderResults();
        refreshFilterUi();
      });

      searchInputEl.addEventListener('input', () => {
        const v = searchInputEl.value;
        if (searchDebounceId !== undefined) window.clearTimeout(searchDebounceId);
        searchDebounceId = window.setTimeout(() => {
          setQuery(v);
        }, 140);
      });
      searchInputEl.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          if (filtersStore.get().q) {
            setQuery('');
          }
          toggleSearch(false);
        } else if (e.key === 'Enter') {
          if (searchDebounceId !== undefined) window.clearTimeout(searchDebounceId);
          setQuery(searchInputEl.value);
          searchInputEl.blur();
        }
      });
      rootEl.querySelector<HTMLButtonElement>('[data-action="search-input-clear"]')?.addEventListener('click', () => {
        searchInputEl.value = '';
        setQuery('');
        searchInputEl.focus();
      });
      rootEl.querySelector<HTMLButtonElement>('[data-action="search-close"]')?.addEventListener('click', () => {
        toggleSearch(false);
      });

      refreshFilterUi();
      // Restore visual state of search bar if a query is already set (e.g., after navigation back).
      if (filtersStore.get().q) {
        searchInputEl.value = filtersStore.get().q;
      }
      // Mark `searchOpen` as used (it's reflected via the .open class on searchBarEl).
      void searchOpen;
      await load();
    },
  };
}
